import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { usePageNameContext } from "../../../contexts/PageTitleContext";
import usePartTagTemplates from "../../../data/usePartTagTemplates";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import Card from "../../../components/shared/Card/Card";
import LoadingWheel from "../../../components/shared/LoadingWheel";
import { ButtonInput } from "../../../components/shared/Inputs/ButtonInput";
import { TextInput } from "../../../components/shared/Inputs/TextInput";
import PartTagList from "../../../components/partTagEditor/PartTagList";
import PartTagFieldGroups from "../../../components/partTagEditor/PartTagFieldGroups";
import PartTagLineModeInput from "../../../components/partTagEditor/PartTagLineModeInput";
import PartTagSizeInputs from "../../../components/partTagEditor/PartTagSizeInputs";
import PartTagCategoriesModal from "../../../components/partTagEditor/PartTagCategoriesModal";
import PartTagPreview from "../../../components/partTagEditor/PartTagPreview";
import { defaultPartLabelDisplay, PartLabelDisplay } from "../../../models/LabelDisplay";
import { IPartTagTemplate } from "../../../models/PartTagTemplate";
import { lineModeFromDisplay, PartTagFormValues, toDisplayRequest } from "../../../utils/partTagFields";

const copyDisplay = (display: PartLabelDisplay): PartLabelDisplay => ({
  ...display,
  vehicleStack: { ...display.vehicleStack },
});

export default function PartTagEditor() {
  const { setInfo } = usePageNameContext();

  useEffect(() => {
    setInfo({
      name: "Part Tags",
      desc: "Choose which fields print on part tags, per category",
    });
  }, [setInfo]);

  return (
    <div className="mt-4">
      <TagsTab />
    </div>
  );
}

function TagsTab() {
  const { tags, create, update, remove, setCategories, resetDefault } = usePartTagTemplates();
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Part Tag");

  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const [categoriesModalOpen, setCategoriesModalOpen] = useState(false);
  const [previewCore, setPreviewCore] = useState(false);
  const [sizeError, setSizeError] = useState<string | undefined>(undefined);

  const allTags = useMemo(() => tags.data?.data ?? [], [tags.data]);
  const defaultTag = allTags.find((t) => t.isDefault);
  const selected: IPartTagTemplate | undefined = allTags.find((t) => t.id === selectedId);

  const { register, handleSubmit, reset, watch, formState } = useForm<PartTagFormValues>({
    defaultValues: {
      name: "",
      lineMode: lineModeFromDisplay(defaultPartLabelDisplay),
      display: copyDisplay(defaultPartLabelDisplay),
    },
  });

  // A new tag starts as a copy of the Default so it only differs where the user changes it.
  useEffect(() => {
    const display = copyDisplay(selected?.display ?? defaultTag?.display ?? defaultPartLabelDisplay);
    reset({ name: selected?.name ?? "", lineMode: lineModeFromDisplay(display), display });
    setSizeError(undefined);
  }, [selected, defaultTag, reset]);

  // Once the list has loaded, land on the Default rather than an empty "create" form.
  useEffect(() => {
    if (selectedId === undefined && defaultTag && !formState.isDirty) setSelectedId(defaultTag.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTag?.id]);

  const watched = watch();
  const previewDisplay = useMemo(
    () => toDisplayRequest(watched.display, watched.lineMode) ?? copyDisplay(defaultPartLabelDisplay),
    [watched]
  );

  const onSubmit = (values: PartTagFormValues) => {
    const display = toDisplayRequest(values.display, values.lineMode);
    if (!display) {
      setSizeError("Width and height must both be greater than zero.");
      return;
    }
    setSizeError(undefined);

    const req = { name: values.name?.trim() ?? "", display };

    if (selected) {
      update.mutate({ id: selected.id, req }, saveHandlers);
    } else {
      create.mutate(req, {
        ...saveHandlers,
        onSuccess: (data) => {
          saveHandlers.onSuccess();
          setSelectedId(data?.data?.id);
        },
      });
    }
  };

  const onDelete = () => {
    if (!selected || selected.isDefault) return;
    remove.mutate(selected.id, {
      ...deleteHandlers,
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setSelectedId(defaultTag?.id);
      },
    });
  };

  const onResetDefault = () => {
    if (!window.confirm("Replace the Default tag's fields and size with the values from the old Admin Settings → Tags tab?")) return;
    resetDefault.mutate(undefined, {
      ...saveHandlers,
      onSuccess: (data) => {
        saveHandlers.onSuccess();
        setSelectedId(data?.data?.id);
      },
    });
  };

  const applyCategories = (categoryIds: string[]) => {
    if (!selected) return;
    setCategories.mutate({ id: selected.id, categoryIds }, saveHandlers);
  };

  if (tags.isLoading) return <LoadingWheel />;

  return (
    <>
      {selected && !selected.isDefault && (
        <PartTagCategoriesModal
          open={categoriesModalOpen}
          setOpen={setCategoriesModalOpen}
          tag={selected}
          tags={allTags}
          onApply={applyCategories}
        />
      )}

      <div className="mb-4 text-gray-600 text-sm">
        <p>
          Each part prints with the tag assigned to its category. Categories not assigned to any tag use
          the Default tag. Core parts keep the core layout but use the same field choices.
        </p>
      </div>

      {!defaultTag && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex items-center justify-between gap-4">
          <div>
            <p className="font-medium">The Default tag hasn't been created yet.</p>
            <p className="mt-1">
              It is built from your previous Tags settings the first time they can be read, and the settings
              service didn't answer. Labels print with the built-in layout until then.
            </p>
          </div>
          <ButtonInput label="Try again" isSubmit={false} onClick={onResetDefault} classes="whitespace-nowrap" isLoading={resetDefault.isLoading} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Tags">
          <PartTagList tags={allTags} selectedId={selectedId} onSelect={setSelectedId} />
        </Card>

        <div className="lg:col-span-2 space-y-4">
          <Card title={selected ? "Tag Details" : "New Tag"}>
            <form className="p-4 space-y-4" onSubmit={handleSubmit(onSubmit)}>
              {selected?.isDefault ? (
                <div className="flex items-center justify-between gap-4">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium text-gray-900">Default</span> — used for every category that is
                    not assigned to another tag. It cannot be renamed or deleted.
                  </div>
                  <ButtonInput
                    label="Reset from previous Tags settings"
                    isSubmit={false}
                    onClick={onResetDefault}
                    classes="whitespace-nowrap bg-white !text-gray-700 ring-1 ring-inset ring-gray-300 hover:!bg-gray-50"
                    isLoading={resetDefault.isLoading}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <TextInput label="Tag Name" register={register} registerName="name" required={true} />
                </div>
              )}

              {selected && !selected.isDefault && (
                <div className="flex items-center justify-between rounded-md border border-gray-200 bg-white p-3">
                  <div>
                    <div className="text-sm font-medium text-gray-700">Categories</div>
                    <div className="text-xs text-gray-500">{selected.categoryIds.length} assigned</div>
                  </div>
                  <ButtonInput label={"Edit Categories"} isSubmit={false} onClick={() => setCategoriesModalOpen(true)} classes="" />
                </div>
              )}

              <PartTagSizeInputs register={register} />
              <PartTagFieldGroups register={register} afterHeader={<PartTagLineModeInput register={register} />} />

              {sizeError && (
                <div className="p-3 rounded border border-red-300 bg-red-50 text-red-800 text-sm">{sizeError}</div>
              )}

              {selected && !selected.isDefault && selected.categoryIds.length === 0 && (
                <div className="p-3 rounded border border-yellow-300 bg-yellow-50 text-yellow-800 text-sm">
                  No categories are assigned to this tag, so nothing prints with it yet.
                </div>
              )}

              <div className="flex items-center justify-between">
                <ButtonInput
                  label={selected ? "Save" : "Create"}
                  isSubmit={true}
                  onClick={undefined}
                  classes=""
                  isLoading={create.isLoading || update.isLoading}
                />
                {selected && !selected.isDefault && (
                  <ButtonInput label={"Delete"} isSubmit={false} onClick={onDelete} classes="bg-red-600 hover:bg-red-700" isLoading={remove.isLoading} />
                )}
              </div>
            </form>
          </Card>

          <Card
            title="Preview"
            subtitle="Representative values, not a real part"
            bodyClassName="p-4"
            headerRight={
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={previewCore}
                  onChange={(e) => setPreviewCore(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Core part
              </label>
            }
          >
            <PartTagPreview display={previewDisplay} isCore={previewCore} />
          </Card>
        </div>
      </div>
    </>
  );
}
